// Where Google sign-in returns to, and whether to sign in with a redirect
// rather than a popup.
//
// Kept free of Firebase imports so it can be unit-tested and read before the
// SDK initialises (lib/firebase.ts uses resolveAuthDomain for its config).
//
// Why this exists (GitHub issue 296): the Firebase sign-in handler
// (`/__/auth/handler`) lives on the `authDomain`. When that is a different
// site from the page — the app on team.onlogic.com, the handler on
// onlogic-team.web.app — Safari keeps the two sites' storage apart, so the
// result the handler stores never reaches the app. And an iPhone home-screen
// app opens a popup in a separate browser sheet that cannot message the app at
// all, so the popup flow there never finishes. The fix is both halves: the
// handler on the app's own site, and a full-page redirect in the home-screen
// app.

/**
 * Hosts that serve this app AND whose `/__/auth/handler` is an authorized
 * redirect URI on the Google OAuth client. On these, the page's own host is
 * the auth domain, so sign-in never crosses sites.
 *
 * Adding a host here requires adding `https://<host>/__/auth/handler` to the
 * OAuth client first (Google Cloud console → APIs & Services → Credentials),
 * or sign-in on that host fails with "redirect_uri_mismatch". See
 * docs/OPS-CHECKLIST.md.
 */
export const SAME_SITE_AUTH_HOSTS: readonly string[] = [
  'team.onlogic.com',
  'onlogic-team.web.app',
  'onlogic-team.firebaseapp.com',
];

/**
 * The auth domain for a page on `hostname`: the host itself when it is one of
 * SAME_SITE_AUTH_HOSTS, otherwise the configured value (preview channels,
 * whose random addresses cannot be registered with Google, and local
 * development).
 */
export function resolveAuthDomain(hostname: string, configured: string): string {
  const host = hostname.toLowerCase();
  return SAME_SITE_AUTH_HOSTS.includes(host) ? host : configured;
}

/**
 * Sign in with a full-page redirect instead of a popup? Only in an iPhone/iPad
 * home-screen app (`navigator.standalone`, which only iOS sets), where the
 * popup cannot report back — and only when the auth domain is the page's own
 * host, because a redirect through another site loses its result in Safari.
 * Everywhere else the popup keeps working as it always has.
 */
export function shouldSignInWithRedirect(opts: {
  standalone: boolean | undefined;
  authDomain: string | undefined;
  host: string;
}): boolean {
  return (
    opts.standalone === true && !!opts.authDomain && opts.authDomain === opts.host.toLowerCase()
  );
}

/**
 * sessionStorage key marking a redirect sign-in in progress. On the way back
 * the app finishes the sign-in only when it is set, so an ordinary launch
 * never loads the popup/redirect machinery (CLAUDE.md § 6, start-up
 * performance).
 */
export const REDIRECT_PENDING_KEY = 'onlogic:auth:redirectPending';

/** Note that a redirect sign-in is starting. Storage failures are ignored. */
export function markRedirectPending(storage: Storage): void {
  try {
    storage.setItem(REDIRECT_PENDING_KEY, '1');
  } catch {
    // Private mode can refuse storage; the redirect then just isn't finished.
  }
}

/** Read and clear the marker: true once per redirect. */
export function takeRedirectPending(storage: Storage): boolean {
  try {
    const pending = storage.getItem(REDIRECT_PENDING_KEY) !== null;
    storage.removeItem(REDIRECT_PENDING_KEY);
    return pending;
  } catch {
    return false;
  }
}
